import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { Plus, TrendingUp, Users, FileText, Clock, Star, Award, BarChart2, Download, Bell, CheckCircle, X, ClipboardList } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalSuppliers: 0,
    totalRFQs: 0,
    pendingRFQs: 0,
    completedRFQs: 0,
    totalPRs: 0,
    pendingPRs: 0,
  });
  const [recentRFQs, setRecentRFQs] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [rfqChartData, setRfqChartData] = useState([]);
  const [prChartData, setPrChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressModalData, setProgressModalData] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [suppliersRes, rfqsRes, rfqStatsRes, prStatsRes, prsRes] = await Promise.all([
        apiClient.get('/suppliers'),
        apiClient.get('/rfqs'),
        apiClient.get('/dashboard/rfq-stats'),
        apiClient.get('/dashboard/pr-stats'),
        apiClient.get('/purchase-requisitions'),
      ]);

      const suppliers = suppliersRes.data;
      const rfqs = rfqsRes.data;
      const prs = prsRes.data || [];

      setStats({
        totalSuppliers: suppliers.length,
        totalRFQs: rfqs.length,
        pendingRFQs: rfqs.filter((r) => r.status === 'sent').length,
        completedRFQs: rfqs.filter((r) => r.status === 'closed').length,
        totalPRs: prs.length,
        pendingPRs: prs.filter((p) => p.status === 'pending' || p.status === 'submitted').length,
      });

      setRecentRFQs(rfqs.slice(0, 5));
      
      // Calculate supplier ratings based on L1 count and response rate
      const supplierRatings = suppliers.map(s => {
        const l1Count = s.l1_count || 0;
        const rfqSentCount = s.rfq_sent_count || 0;
        const responseCount = s.response_count || 0;
        // Calculate response rate - only if we've sent at least 1 RFQ
        const responseRate = rfqSentCount > 0 ? Math.round((responseCount / rfqSentCount) * 100) : 0;
        // Score based on L1 wins (60% weight) and response rate (40% weight)
        const score = (l1Count * 10 * 0.6) + (responseRate * 0.4);
        return { ...s, score, l1Count, responseRate, rfqSentCount, responseCount };
      });
      
      // Sort by score and take top 5
      const sortedSuppliers = supplierRatings.sort((a, b) => b.score - a.score).slice(0, 5);
      setTopSuppliers(sortedSuppliers);
      
      // Fetch recent quotes
      try {
        const quotesRes = await apiClient.get('/dashboard/recent-quotes');
        setRecentQuotes(quotesRes.data);
      } catch (e) {
        console.log('Recent quotes endpoint not available');
      }
      
      // Set RFQ chart data
      setRfqChartData(rfqStatsRes.data);
      
      // Set PR chart data
      setPrChartData(prStatsRes.data || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (rfqId) => {
    try {
      const response = await apiClient.get(`/comparison/${rfqId}/export?format=pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comparison_${rfqId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Make sure there are quotes to compare.');
    }
  };

  // Progress Modal - fetch sent suppliers and their status
  const handleShowProgress = async (rfq) => {
    try {
      const sentRes = await apiClient.get(`/rfqs/${rfq.id}/sent-suppliers`);
      setProgressModalData({
        rfq,
        suppliers: sentRes.data
      });
      setShowProgressModal(true);
    } catch (error) {
      console.error('Error fetching progress:', error);
      toast.error('Failed to load progress details');
    }
  };

  // Send reminder to supplier
  const handleSendReminder = async (rfqId, supplierId) => {
    setSendingReminder(supplierId);
    try {
      await apiClient.post(`/rfqs/${rfqId}/send-reminder/${supplierId}`);
      toast.success('Reminder sent successfully');
      // Refresh the progress data
      if (progressModalData) {
        const sentRes = await apiClient.get(`/rfqs/${rfqId}/sent-suppliers`);
        setProgressModalData({
          ...progressModalData,
          suppliers: sentRes.data
        });
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <div className="text-slate-500">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">Overview of your procurement system</p>
        </div>
        <div className="flex gap-3">
          <Link to="/purchase-requisitions?create=true">
            <button className="btn btn-secondary">
              <ClipboardList size={18} />
              Create PR
            </button>
          </Link>
          <Link to="/rfqs?create=true" data-testid="create-rfq-btn">
            <button className="btn btn-primary">
              <Plus size={18} />
              Create RFQ
            </button>
          </Link>
        </div>
      </div>

      {/* Stats Cards - Compact 6-column grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Link to="/suppliers" className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid="stat-total-suppliers">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="text-blue-600" size={20} />
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-slate-900">{stats.totalSuppliers}</div>
              <div className="text-xs text-slate-500">Suppliers</div>
            </div>
          </div>
        </Link>

        <Link to="/rfqs" className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid="stat-total-rfqs">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <FileText className="text-purple-600" size={20} />
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-slate-900">{stats.totalRFQs}</div>
              <div className="text-xs text-slate-500">Total RFQs</div>
            </div>
          </div>
        </Link>

        <Link to="/rfqs?status=sent" className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid="stat-pending-rfqs">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 rounded-lg">
              <Clock className="text-orange-600" size={20} />
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-slate-900">{stats.pendingRFQs}</div>
              <div className="text-xs text-slate-500">Pending RFQs</div>
            </div>
          </div>
        </Link>

        <Link to="/rfqs?status=closed" className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid="stat-completed-rfqs">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle className="text-green-600" size={20} />
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-slate-900">{stats.completedRFQs}</div>
              <div className="text-xs text-slate-500">Completed</div>
            </div>
          </div>
        </Link>

        <Link to="/purchase-requisitions" className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid="stat-total-prs">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <ClipboardList className="text-indigo-600" size={20} />
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-slate-900">{stats.totalPRs}</div>
              <div className="text-xs text-slate-500">Total PRs</div>
            </div>
          </div>
        </Link>

        <Link to="/purchase-requisitions?status=pending" className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid="stat-pending-prs">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-lg">
              <Clock className="text-amber-600" size={20} />
            </div>
            <div>
              <div className="font-mono text-2xl font-bold text-slate-900">{stats.pendingPRs}</div>
              <div className="text-xs text-slate-500">Pending PRs</div>
            </div>
          </div>
        </Link>
      </div>

      {/* Side-by-side Charts - RFQs and PRs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* RFQ Chart */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="rfq-chart-section">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold tracking-tight text-slate-900 flex items-center gap-2">
              <BarChart2 size={18} className="text-blue-600" />
              RFQs Created (Last 7 Days)
            </h2>
          </div>
          <div className="p-4">
            {rfqChartData.length > 0 ? (
              <div className="h-40 flex items-end gap-2">
                {rfqChartData.map((day, idx) => {
                  const maxCount = Math.max(...rfqChartData.map(d => d.count), 1);
                  const heightPercent = (day.count / maxCount) * 100;
                  const isToday = idx === rfqChartData.length - 1;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div className={`text-xs font-bold mb-1 ${day.count > 0 ? 'text-blue-600' : 'text-slate-300'}`}>
                        {day.count}
                      </div>
                      <div className="relative w-full flex-1 flex items-end">
                        <div 
                          className={`w-full rounded-t transition-all ${
                            day.count > 0 
                              ? isToday ? 'bg-blue-600' : 'bg-blue-400'
                              : 'bg-slate-100'
                          }`}
                          style={{ 
                            height: day.count > 0 ? `${Math.max(heightPercent, 15)}%` : '4px',
                            minHeight: day.count > 0 ? '16px' : '4px'
                          }}
                        />
                      </div>
                      <div className={`mt-2 text-xs text-center ${isToday ? 'font-bold text-blue-600' : 'text-slate-500'}`}>
                        {day.label.split(' ')[0]}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
                No RFQ data available
              </div>
            )}
          </div>
        </div>

        {/* PR Chart */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="pr-chart-section">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold tracking-tight text-slate-900 flex items-center gap-2">
              <BarChart2 size={18} className="text-indigo-600" />
              PRs Generated (Last 7 Days)
            </h2>
          </div>
          <div className="p-4">
            {prChartData.length > 0 ? (
              <div className="h-40 flex items-end gap-2">
                {prChartData.map((day, idx) => {
                  const maxCount = Math.max(...prChartData.map(d => d.count), 1);
                  const heightPercent = (day.count / maxCount) * 100;
                  const isToday = idx === prChartData.length - 1;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div className={`text-xs font-bold mb-1 ${day.count > 0 ? 'text-indigo-600' : 'text-slate-300'}`}>
                        {day.count}
                      </div>
                      <div className="relative w-full flex-1 flex items-end">
                        <div 
                          className={`w-full rounded-t transition-all ${
                            day.count > 0 
                              ? isToday ? 'bg-indigo-600' : 'bg-indigo-400'
                              : 'bg-slate-100'
                          }`}
                          style={{ 
                            height: day.count > 0 ? `${Math.max(heightPercent, 15)}%` : '4px',
                            minHeight: day.count > 0 ? '16px' : '4px'
                          }}
                        />
                      </div>
                      <div className={`mt-2 text-xs text-center ${isToday ? 'font-bold text-indigo-600' : 'text-slate-500'}`}>
                        {day.label.split(' ')[0]}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
                No PR data available
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm mb-6" data-testid="recent-rfqs-section">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Recent RFQs</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  RFQ Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Quote Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {recentRFQs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-500" data-testid="no-rfqs-message">
                    No RFQs created yet. Create your first RFQ to get started.
                  </td>
                </tr>
              ) : (
                recentRFQs.map((rfq) => {
                  const additionalItems = rfq.items || [];
                  const totalItems = 1 + additionalItems.length;
                  const quotesReceived = rfq.quotes_count || 0;
                  const totalSent = rfq.suppliers_sent_count || 0;
                  const progressPercent = totalSent > 0 ? Math.round((quotesReceived / totalSent) * 100) : 0;
                  
                  return (
                  <tr key={rfq.id} className="hover:bg-slate-50" data-testid={`rfq-row-${rfq.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-mono font-medium text-slate-900">{rfq.rfq_number}</span>
                      {totalItems > 1 && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {totalItems} items
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-slate-900">
                        <span className="text-blue-600 font-semibold">1.</span> {rfq.item_name}
                        <span className="text-slate-500 font-mono text-xs ml-2">({rfq.quantity} {rfq.unit})</span>
                      </div>
                      {additionalItems.map((item, idx) => (
                        <div key={item.id} className="text-sm text-slate-700 mt-1">
                          <span className="text-green-600 font-semibold">{idx + 2}.</span> {item.item_name}
                          <span className="text-slate-500 font-mono text-xs ml-2">({item.quantity} {item.unit})</span>
                        </div>
                      ))}
                    </td>
                    <td className="px-6 py-4">
                      {totalSent > 0 ? (
                        <div 
                          className="w-32 cursor-pointer hover:opacity-80"
                          onClick={() => handleShowProgress(rfq)}
                          title="Click to view submission details"
                          data-testid={`progress-bar-${rfq.id}`}
                        >
                          <div className="flex justify-between text-xs text-slate-600 mb-1">
                            <span>{quotesReceived} of {totalSent}</span>
                            <span>{progressPercent}%</span>
                          </div>
                          <Progress value={progressPercent} className="h-2" />
                          <p className="text-xs text-blue-600 mt-1">Click for details</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Not sent</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`badge ${
                          rfq.status === 'sent'
                            ? 'badge-info'
                            : rfq.status === 'completed' || rfq.status === 'closed'
                            ? 'badge-success'
                            : 'badge-warning'
                        }`}
                        data-testid={`rfq-status-${rfq.id}`}
                      >
                        {rfq.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {new Date(rfq.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Link to={`/rfqs/${rfq.id}`} data-testid={`view-rfq-${rfq.id}`}>
                          <button className="text-sm text-slate-900 font-medium hover:text-slate-700">
                            View →
                          </button>
                        </Link>
                        {rfq.received_count > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadPDF(rfq.id)}
                            className="text-xs h-7 px-2"
                            title="Download Quote Comparison PDF"
                          >
                            <Download size={12} className="mr-1" />
                            PDF
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Recent Quotes & Top Suppliers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Quotes from Suppliers */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="recent-quotes-section">
          <div className="p-5 border-b border-slate-200 flex items-center gap-2">
            <FileText className="text-purple-600" size={20} />
            <h2 className="text-lg font-semibold tracking-tight text-slate-900">Recent Quotes (Top 10)</h2>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            {recentQuotes.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                No quotes received yet.
              </div>
            ) : (
              <div className="space-y-3">
                {recentQuotes.map((quote) => (
                  <div key={quote.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 text-sm truncate">{quote.supplier_name}</div>
                      <div className="text-xs text-slate-600 truncate">{quote.item_name}</div>
                      <div className="text-xs text-slate-500">
                        {quote.rfq_number} • Rate: <span className="font-mono text-green-600">{quote.rate}</span>
                      </div>
                    </div>
                    <div className="text-right ml-2 flex-shrink-0">
                      <div className="text-xs text-slate-400">
                        {new Date(quote.submitted_at).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                        })}
                      </div>
                      <Link to={`/rfqs/${quote.rfq_id}`} className="text-xs text-blue-600 hover:underline">
                        View →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Supplier Performance Ratings */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="supplier-ratings-section">
          <div className="p-5 border-b border-slate-200 flex items-center gap-2">
            <Award className="text-yellow-600" size={20} />
            <h2 className="text-lg font-semibold tracking-tight text-slate-900">Top Performing Suppliers</h2>
          </div>
          <div className="p-4">
            {topSuppliers.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                No supplier data available yet.
              </div>
            ) : (
              <div className="space-y-3">
                {topSuppliers.map((supplier, idx) => (
                  <div key={supplier.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                      idx === 1 ? 'bg-slate-200 text-slate-700' :
                      idx === 2 ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 text-sm">{supplier.name}</div>
                      <div className="text-xs text-slate-500">{supplier.category || 'General'}</div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm">
                        <Star className="text-yellow-500" size={14} fill="currentColor" />
                        <span className="font-semibold text-slate-900">{supplier.l1Count}</span>
                        <span className="text-xs text-slate-500">L1 wins</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <BarChart2 size={12} />
                        {supplier.rfqSentCount > 0 ? (
                          <span>{supplier.responseRate}% ({supplier.responseCount}/{supplier.rfqSentCount})</span>
                        ) : (
                          <span className="text-slate-400">No RFQs sent</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Modal - Show supplier submission status */}
      <Dialog open={showProgressModal} onOpenChange={setShowProgressModal}>
        <DialogContent className="max-w-2xl" data-testid="dashboard-progress-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={20} />
              Quote Submission Progress
            </DialogTitle>
            <DialogDescription>
              {progressModalData?.rfq?.rfq_number} - {progressModalData?.rfq?.item_name}
            </DialogDescription>
          </DialogHeader>
          
          {progressModalData && (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {progressModalData.suppliers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No suppliers have been sent this RFQ yet.
                </div>
              ) : (
                <>
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{progressModalData.suppliers.length}</div>
                      <div className="text-xs text-slate-500">Sent</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {progressModalData.suppliers.filter(s => s.status === 'submitted').length}
                      </div>
                      <div className="text-xs text-slate-500">Submitted</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-amber-600">
                        {progressModalData.suppliers.filter(s => s.status !== 'submitted').length}
                      </div>
                      <div className="text-xs text-slate-500">Pending</div>
                    </div>
                  </div>

                  {/* Supplier List */}
                  <div className="space-y-2">
                    {progressModalData.suppliers.map((supplier) => (
                      <div
                        key={supplier.supplier_id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          supplier.status === 'submitted'
                            ? 'bg-green-50 border-green-200'
                            : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {supplier.status === 'submitted' ? (
                            <CheckCircle size={18} className="text-green-600" />
                          ) : (
                            <Clock size={18} className="text-amber-600" />
                          )}
                          <div>
                            <div className="font-medium text-slate-900 text-sm">{supplier.supplier_name}</div>
                            <div className="text-xs text-slate-500">{supplier.supplier_email}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            supplier.status === 'submitted'
                              ? 'bg-green-200 text-green-800'
                              : 'bg-amber-200 text-amber-800'
                          }`}>
                            {supplier.status === 'submitted' ? 'Submitted' : 'Pending'}
                          </span>
                          {supplier.status !== 'submitted' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendReminder(progressModalData.rfq.id, supplier.supplier_id)}
                              disabled={sendingReminder === supplier.supplier_id}
                              className="text-xs h-7 px-2 border-amber-400 text-amber-700 hover:bg-amber-100"
                              data-testid={`send-reminder-${supplier.supplier_id}`}
                            >
                              <Bell size={12} className="mr-1" />
                              {sendingReminder === supplier.supplier_id ? 'Sending...' : 'Remind'}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowProgressModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;